import {
	NODE_CATEGORIES,
	NODE_TYPE_LABELS,
	type NodeType,
	summarizeNode,
} from "@velizhanin-dev/bot-scenario"
import { Search } from "lucide-react"
import { useMemo, useState } from "react"
import type { ScenarioDraft } from "@/entities/bot-builder"
import { CATEGORY_APPEARANCE, NODE_ICONS } from "@/entities/bot-builder"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"
import { ToolButton } from "@/shared/ui/tool-button"

const SEARCH_LIMIT = 12

function NodeTile({ type }: { type: NodeType }) {
	const Icon = NODE_ICONS[type]
	return (
		<span
			className={cn(
				"grid h-7 w-7 shrink-0 place-items-center rounded-lg",
				CATEGORY_APPEARANCE[NODE_CATEGORIES[type] ?? "action"].tile,
			)}
		>
			{Icon ? <Icon className="h-3.5 w-3.5" /> : null}
		</span>
	)
}

/** Поиск блока по тексту, типу и заметке во всех потоках. ⌘K открывает общий поиск, он тоже находит блоки. */
export function BlockSearch({
	draft,
	onPick,
	triggerClassName,
}: {
	draft: ScenarioDraft
	onPick: (nodeId: string) => void
	triggerClassName?: string
}) {
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState("")

	const needle = query.trim().toLowerCase()
	const results = useMemo(() => {
		if (needle.length < 2) return []
		const flowNames = new Map(draft.flows.map((flow) => [flow.id, flow.name]))
		return draft.nodes
			.map((node) => ({
				node,
				summary: summarizeNode(node, draft.flows, draft.tags),
				label: NODE_TYPE_LABELS[node.type as NodeType] ?? node.type,
				flow: flowNames.get(node.flowId) ?? "",
			}))
			.filter(({ node, summary, label }) =>
				[summary, label, node.note ?? ""].some((text) => text.toLowerCase().includes(needle)),
			)
			.slice(0, SEARCH_LIMIT)
	}, [draft, needle])

	const close = () => {
		setOpen(false)
		setQuery("")
	}
	const pick = (nodeId: string) => {
		close()
		onPick(nodeId)
	}

	return (
		<Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
			<PopoverTrigger asChild>
				<ToolButton label="Найти блок" className={triggerClassName}>
					<Search />
				</ToolButton>
			</PopoverTrigger>
			<PopoverContent
				side="top"
				sideOffset={14}
				className="w-[min(92vw,400px)] overflow-hidden rounded-2xl p-0"
			>
				<div className="flex items-center gap-2 border-b border-border px-3">
					<Search className="h-4 w-4 shrink-0 text-muted-foreground" />
					{/* Фокус в поле ставит сам Popover: это первый фокусируемый элемент. */}
					<input
						aria-label="Поиск блока"
						placeholder="Текст, тип блока или заметка"
						className="h-11 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground md:text-sm"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter" && results[0]) pick(results[0].node.id)
						}}
					/>
				</div>
				<div className="max-h-80 overflow-y-auto p-1">
					{needle.length < 2 ? (
						<p className="px-3 py-6 text-center text-xs text-muted-foreground">
							Введите хотя бы два символа
						</p>
					) : results.length === 0 ? (
						<p className="px-3 py-6 text-center text-xs text-muted-foreground">Ничего не нашлось</p>
					) : (
						results.map(({ node, summary, label, flow }) => (
							<Button
								key={node.id}
								type="button"
								variant="ghost"
								className="h-auto w-full justify-start gap-3 rounded-xl px-2 py-1.5 text-left"
								onClick={() => pick(node.id)}
							>
								<NodeTile type={node.type as NodeType} />
								<span className="min-w-0">
									<span className="block truncate text-sm">{summary}</span>
									<span className="block truncate text-xs text-muted-foreground">
										{label} · {flow}
									</span>
								</span>
							</Button>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	)
}
